import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, ArrowRight, Clock, ShieldCheck } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";
import MethodDiagram from "@/components/marketing/MethodDiagram";
import { http, trackEvent } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout, courseItem } from "@/lib/checkout";
import { useSeo, StructuredData } from "@/lib/seo";
import { money } from "@/lib/constants";

const TITLES = {
  diagnostic: "Infant Sleep Diagnostic Near Me | $99 Human-Reviewed",
  blueprint: "Personalized Infant Sleep Blueprint | $279 Complete Plan",
  guided: "Guided Virtual Consulting | $749 (Includes Clarity & Blueprint)",
  inhome_overnight: "In-Home + Overnight Infant Sleep Consultant | $2,549",
};

const FR_LABELS = [
  ["included", "What's included"], ["not_included", "What's not included"], ["turnaround", "Expected turnaround"],
  ["support_duration", "Support duration"], ["professional_contacts", "Professional contacts"],
  ["contact_method", "Contact method"], ["response_window", "Response window"],
  ["rescheduling", "Rescheduling"], ["extensions", "Extensions"], ["if_paused", "If you pause"],
  ["if_child_ill", "If your child is ill"], ["if_operator_unavailable", "If your Certified Infant Sleep Consultant is unavailable"],
  ["refund_policy", "Refund / cancellation"], ["geographic_limits", "Geographic limits"],
];

export default function ProductPage({ productKey }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [clarityDone, setClarityDone] = useState(false);

  useEffect(() => { http.get(`/products/${productKey}`).then((r) => setP(r.data)).catch(() => {}); }, [productKey]);
  useEffect(() => { if (productKey === "inhome_overnight") trackEvent("in_home_overnight_view", {}); }, [productKey]);
  useEffect(() => { trackEvent("view_item", { currency: "USD", ...courseItem(productKey), item_list_name: "Consulting pages" }, { once: true, key: `view-${productKey}` }); }, [productKey]);
  useEffect(() => {
    if (user?.role === "customer") http.get("/portal/eligibility").then((r) => setClarityDone(r.data.clarity_completed)).catch(() => {});
  }, [user]);
  useSeo({ title: TITLES[productKey], description: p?.description, canonical: window.location.pathname });

  if (!p) return <MarketingShell><div className="max-w-4xl mx-auto px-5 py-32 text-[#A39E98]">Loading…</div></MarketingShell>;

  const avail = p.availability_status || {};
  const canBuy = avail.status === "available";
  const isInhome = productKey === "inhome_overnight";
  const blueprintGated = productKey === "blueprint" && !clarityDone;
  const fr = p.fulfillment_rules || {};

  return (
    <MarketingShell>
      <StructuredData data={{ "@context": "https://schema.org", "@type": "Service", name: p.name,
        description: p.description, offers: { "@type": "Offer", price: p.price, priceCurrency: "USD" } }} />
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-14">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 items-start">
          <div className="fade-up">
            <div className="eyebrow text-[#C2836B] mb-4">Level {p.level} · {p.purpose}</div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#F7F5F0] tracking-tight leading-[1.08]">{p.name}</h1>
            <p className="font-display text-xl text-[#A39E98] italic mt-4">{p.tagline}</p>
            <p className="text-[#A39E98] text-[17px] leading-relaxed mt-6 max-w-xl">{p.description}</p>
            <p className="text-[#738075] text-[15px] leading-relaxed mt-4 max-w-xl">Whether you searched for an infant sleep consultant near me or simply want expert help tonight, this service is delivered by a Certified Infant Sleep Consultant — virtually anywhere, with in-home support available in select areas.</p>
            <ul className="grid sm:grid-cols-2 gap-2.5 mt-8">
              {(p.features || []).map((f) => (
                <li key={f} className="flex gap-2 text-[15px] text-[#F7F5F0]">
                  <Check size={16} className="text-[#738075] mt-1 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="card-night rounded-2xl p-7 lg:sticky lg:top-24 fade-up" style={{ animationDelay: "120ms" }}>
            <div className="font-display text-4xl text-[#F7F5F0]">{money(p.price)}</div>
            <p className="text-[#738075] text-sm mt-1">One-time · standalone product</p>
            {blueprintGated ? (
              <>
                <div className="text-[#C49A5A] text-sm mt-5 flex items-center gap-2"><Clock size={15} /> Available after your Clarity stage</div>
                <p className="text-[#A39E98] text-sm mt-2">Your personalized plan is built from what your Certified Infant Sleep Consultant learns during Clarity.</p>
                <button data-testid="product-start-clarity" onClick={() => startCheckout({ product: "diagnostic", user, navigate: nav })}
                  className="btn-clay w-full py-3.5 rounded-full text-[15px] mt-4 flex items-center justify-center gap-2">Start With Clarity — $99 <ArrowRight size={16} /></button>
              </>
            ) : isInhome && !canBuy ? (
              <>
                <div className="text-[#C49A5A] text-sm mt-5 flex items-center gap-2"><Clock size={15} /> {avail.status === "waitlist" ? "Currently at capacity" : "Availability varies by area"}</div>
                <button data-testid="product-inquire" onClick={() => nav("/in-home-infant-sleep-consulting")} className="btn-outline-night w-full py-3 rounded-full text-sm mt-4">Request Availability</button>
              </>
            ) : (
              <button data-testid="product-buy" onClick={() => startCheckout({ product: p.key, user, navigate: nav })}
                className="btn-clay w-full py-3.5 rounded-full text-[15px] mt-5 flex items-center justify-center gap-2">
                {p.cta} <ArrowRight size={16} />
              </button>
            )}
            {canBuy && avail.spots_left <= 3 && <p className="text-[#8A998C] text-xs mt-3 text-center">{avail.spots_left} spots left this cycle</p>}
            <p className="text-[#6b665e] text-xs mt-5 leading-relaxed">No discounted upgrades and no product credits. Each service is complete on its own.</p>
            <div className="flex items-center gap-2 text-[#738075] text-xs mt-4"><ShieldCheck size={14} /> Not medical care.</div>
          </div>
        </div>
      </section>

      {/* Fulfillment boundaries */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14 border-t border-[#2E2C2A]">
        <h2 className="font-display text-3xl text-[#F7F5F0] mb-2">Exactly what you can expect</h2>
        <p className="text-[#A39E98] mb-8 max-w-xl">We don't leave expectations vague. Here are the boundaries for this service.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px rounded-lg overflow-hidden border border-[#2E2C2A]">
          {FR_LABELS.map(([k, label]) => fr[k] !== undefined && (
            <div key={k} className="bg-[#1C1B1A] p-5">
              <div className="eyebrow text-[#738075] mb-2">{label}</div>
              <div className="text-[#F7F5F0] text-sm leading-relaxed">
                {typeof fr[k] === "boolean" ? (fr[k] ? <span className="flex items-center gap-1 text-[#8A998C]"><Check size={14}/> Yes</span> : <span className="flex items-center gap-1 text-[#6b665e]"><X size={14}/> No</span>) : fr[k]}
              </div>
            </div>
          ))}
        </div>
        {fr.notes && <p className="text-[#A39E98] text-sm mt-6 italic">{fr.notes}</p>}
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14 border-t border-[#2E2C2A]">
        <div className="eyebrow text-[#C2836B] mb-4">How it fits the method</div>
        <MethodDiagram />
      </section>
    </MarketingShell>
  );
}
